import type { VisibleApplication } from '~~/shared/schemas/applicationLauncher'
import type { $Fetch } from 'ofetch'

interface ApplicationLauncherResponse {
  applications: VisibleApplication[]
  contextKey?: string
}

export function applicationLauncherCacheKey(tenantId?: string | null, userId?: string | null) {
  return `application-launcher:${tenantId ?? 'unresolved'}:${userId ?? 'anonymous'}`
}

export function useApplicationLauncher() {
  const auth = useAuth()
  const { tenant, user, contextKey, switching, contextUnconfirmed } = auth
  const key = computed(
    () => `${applicationLauncherCacheKey(tenant.value?.id, user.value?.id)}:${contextKey.value}`,
  )
  const requestFetch = useRequestFetch() as $Fetch
  const result = useAsyncData<ApplicationLauncherResponse>(
    key,
    async () => {
      const requestedContext = contextKey.value
      if (switching.value || contextUnconfirmed.value)
        return { applications: [], contextKey: requestedContext }
      const response = await requestFetch<ApplicationLauncherResponse>(
        '/api/application-launcher',
        { cache: 'no-store' },
      )
      return {
        applications: requestedContext === contextKey.value ? response.applications : [],
        contextKey: requestedContext,
      }
    },
    { dedupe: 'cancel', lazy: true, watch: [switching, contextUnconfirmed] },
  )

  return {
    applications: computed(() =>
      !switching.value &&
      !contextUnconfirmed.value &&
      result.data.value?.contextKey === contextKey.value
        ? result.data.value.applications
        : [],
    ),
    status: computed(() =>
      auth.pending.value || switching.value ? 'pending' : result.status.value,
    ),
    error: result.error,
    refresh: async () => {
      result.clear()
      await auth.refresh()
      await result.refresh()
    },
  }
}
